import React, { useState, useEffect, useRef } from 'react'
import { BrManageContentButton } from '@bloomreach/react-sdk'
import CarouselCard from './carouselCard.jsx'
import { halApi } from '../utilities/utilities.js'
import { isLocal, getMargins } from '../../utility'
import './carousel.css'

const CarouselWrapper = ({ page, component }) => {
  const _parameters = component?.getParameters()
  const parameters = {
    marginTop: _parameters?.marginTop || 'No Spacing',
    marginBottom: _parameters?.marginBottom || 'No Spacing',
  }
  const slideType = _parameters?.slideType || 'CountrySlides'
  const SlideComponent = CarouselCard
  const desktopSlidesPerView = parseInt(_parameters?.slidesPerView, 10)
  const desktopSlidesPerScroll = parseInt(_parameters?.slidesPerScroll, 10)

  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const slidesPerView =
    screenWidth <= 768 ? 1 : screenWidth <= 1024 ? 2 : desktopSlidesPerView
  const slidesPerScroll =
    screenWidth <= 768 ? 1 : screenWidth <= 1024 ? 2 : desktopSlidesPerScroll

  const [slides, setSlides] = useState([])

  const normalizeBodyContent = (body) => {
    if (Array.isArray(body)) {
      return body
        .map((item) => (typeof item === 'string' ? item : item?.content))
        .filter(Boolean)
    }

    if (typeof body === 'string') {
      return [body]
    }

    if (body && typeof body === 'object' && typeof body.content === 'string') {
      return [body.content]
    }

    return []
  }

  const [currentIndex, setCurrentIndex] = useState(0)
  const touchStartX = useRef(null)
  const touchEndX = useRef(null)

  useEffect(() => {
    const scope = _parameters?.document
    if (!scope) return

    const fetchSlides = async () => {
      const folderPath = scope.replace(/^\/content\/documents\/[^/]+\//, '')
      const response = await halApi('documents', folderPath)
      const docs = response?._embedded?.documents || []
      if (docs.length) {
        const cmsSlides = docs
          .map((doc) => ({
            order: parseInt(doc.order, 10) || 0,
            title: doc.grouptitle,
            heading: doc.heading,
            image: doc.ImageCompound?.image?._meta?.mirror?.path
              ? `${isLocal() ? '/cti-site' : ''}/binaries/${doc.ImageCompound.image._meta.mirror.path}`
              : '',
            imageAlt: doc.ImageCompound?.alt || doc.heading,
            body: normalizeBodyContent(doc.body),
          }))
          .sort((a, b) => a.order - b.order)
        setSlides(cmsSlides)
      }
    }

    fetchSlides()
  }, [_parameters?.document])

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - slidesPerScroll))
  }

  const handleNext = () => {
    setCurrentIndex((prev) =>
      Math.min(slides.length - slidesPerView, prev + slidesPerScroll)
    )
  }

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
    touchEndX.current = null
  }

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return
    const diff = touchStartX.current - touchEndX.current
    if (Math.abs(diff) > 50) {
      if (diff > 0) handleNext()
      else handlePrev()
    }
    touchStartX.current = null
    touchEndX.current = null
  }

  const slideWidthPercent = 100 / slidesPerView
  const translateX = -(currentIndex * slideWidthPercent)

  return (
    <div
      className={`Carousel u-sizeConstrained u-sm-sizeConstrainedFlush u-paddingBottom10gu ${getMargins(parameters.marginTop, parameters.marginBottom)}`}
      data-analytics="carousel-component-container"
    >
      <h3 className="Carousel-title u-sm-marginLeft5gu u-marginLeft10gu u-paddingBottom3gu">
        {slides[currentIndex]?.title}
      </h3>
      <div
        className="Carousel-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button
          className="Carousel-btn Carousel-btn--inline u-textSizeMsrp4 u-textColorBrandDark8"
          onClick={handlePrev}
          disabled={currentIndex === 0}
          aria-label="Previous"
          data-analytics="carousel-component-cta-previous"
        >
          &#8249;
        </button>
        <div className="Carousel-track">
          <div
            className="Carousel-slider"
            style={{ transform: `translateX(${translateX}%)` }}
          >
            {slides.map((slide, index) => (
              <div
                key={index}
                className="Carousel-slideWrapper"
                style={{ width: `${slideWidthPercent}%` }}
              >
                <SlideComponent slide={slide} />
              </div>
            ))}
          </div>
        </div>
        <button
          className="Carousel-btn Carousel-btn--inline u-textSizeMsrp4 u-textColorBrandDark8"
          onClick={handleNext}
          disabled={currentIndex >= slides.length - slidesPerView}
          aria-label="Next"
          data-analytics="carousel-component-cta-next"
        >
          &#8250;
        </button>
      </div>
      <div className="Carousel-controls u-lg-hidden">
        <button
          className="Carousel-btn Carousel-btn--bottom u-textSizeMsrp4 u-textColorBrandDark8"
          onClick={handlePrev}
          disabled={currentIndex === 0}
          aria-label="Previous"
          data-analytics="carousel-component-cta-previous"
        >
          &#8249;
        </button>
        <button
          className="Carousel-btn Carousel-btn--bottom u-textSizeMsrp4 u-textColorBrandDark8"
          onClick={handleNext}
          disabled={currentIndex >= slides.length - slidesPerView}
          aria-label="Next"
          data-analytics="carousel-component-cta-next"
        >
          &#8250;
        </button>
        <span className="Carousel-indicator u-textColorBrandDark8">
          {Math.floor(currentIndex / slidesPerScroll) + 1} /{' '}
          {Math.ceil((slides.length - slidesPerView) / slidesPerScroll) + 1}
        </span>
      </div>
    </div>
  )
}

export default CarouselWrapper
